import { useEffect } from 'react';

export default function MetaTags({
  title,
  description,
  image,
  url,
  type = 'article',
  author = 'VBall Stats',
  publishedTime,
  modifiedTime,
  section,
  tags
}) {
  useEffect(() => {
    // Set default values
    const siteTitle = 'VBall Stats - Track Your Volleyball Performance';
    const defaultDescription = 'Track your volleyball statistics and improve your game with VBall Stats. Free volleyball stat tracking for players, coaches, and teams.';
    const defaultImage = window.location.origin + '/Vballstat-card.png';
    const currentUrl = url || window.location.href;

    // Update document title
    document.title = title ? `${title} | VBall Stats` : siteTitle;

    // Helper function to set or update meta tag
    const setMetaTag = (attribute, attributeValue, content) => {
      let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Standard meta tags
    setMetaTag('name', 'description', description || defaultDescription);
    if (author) {
      setMetaTag('name', 'author', author);
    }

    // Open Graph tags (Facebook, LinkedIn, etc.)
    setMetaTag('property', 'og:title', title || siteTitle);
    setMetaTag('property', 'og:description', description || defaultDescription);
    setMetaTag('property', 'og:image', image || defaultImage);
    setMetaTag('property', 'og:url', currentUrl);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:site_name', 'VBall Stats');

    // Article-specific Open Graph tags
    if (type === 'article') {
      if (publishedTime) {
        setMetaTag('property', 'article:published_time', publishedTime);
      }
      if (modifiedTime) {
        setMetaTag('property', 'article:modified_time', modifiedTime);
      }
      if (author) {
        setMetaTag('property', 'article:author', author);
      }
      if (section) {
        setMetaTag('property', 'article:section', section);
      }
      if (tags && tags.length > 0) {
        // Remove existing article:tag tags
        const existingTags = document.querySelectorAll('meta[property="article:tag"]');
        existingTags.forEach(tag => tag.remove());
        // Add new tags
        tags.forEach(tag => {
          const element = document.createElement('meta');
          element.setAttribute('property', 'article:tag');
          element.setAttribute('content', tag);
          document.head.appendChild(element);
        });
      }
    }

    // Twitter Card tags
    setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', title || siteTitle);
    setMetaTag('name', 'twitter:description', description || defaultDescription);
    setMetaTag('name', 'twitter:image', image || defaultImage);
    setMetaTag('name', 'twitter:site', '@VBallStats'); // Update with your actual Twitter handle
    if (author && author !== 'VBall Stats') {
      setMetaTag('name', 'twitter:creator', '@VBallStats'); // Update if you want individual author handles
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', currentUrl);

    // Additional SEO tags
    setMetaTag('name', 'robots', 'index, follow');
    setMetaTag('name', 'viewport', 'width=device-width, initial-scale=1.0');

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      document.title = siteTitle;
    };
  }, [title, description, image, url, type, author, publishedTime, modifiedTime, section, tags]);

  return null; // This component doesn't render anything
}
